import { useEffect, useState, useCallback, createContext, useContext } from 'react'
import { PokerTableSocket } from './components/poker-table/PokerTableSocket'
import { TopBar } from './components/ui/TopBar'
import { useTelegram } from './hooks/useTelegram'

const LOCALE_MAP: Record<string, Record<string, string>> = {
  zh: {
    'app.loading': '加载中...',
    'app.start': '开始游戏',
    'app.starting': '开始中...',
    'app.replay': '再来一局',
    'app.share': '📤 分享',
    'app.sharing': '分享中...',
    'app.history': '📋 记录',
    'app.leaderboard': '🏆 排行榜',
    'app.glory': '👑 我的荣耀',
    'app.balance': '您当前余额',
    'app.buyin': '双方每局各投入',
    'app.win': '🎉 你赢了 {name}！',
    'app.lose': '😞 你输了',
    'app.tie': '🤝 平局！',
    'app.revealing': '正在亮牌...',
    'app.thinking': '思考中...',
    'app.chips': '筹码',
    'app.your_turn': '轮到你了',
    'app.waiting': '等待对手...',
    'app.error_network': '网络错误，请重试',
    'app.error_balance': '余额不足',
    'app.share_text': '我在♠️ 德州扑克中{result} {pot} 筹码{hand}！来和我对战吧！',
    'app.share_win': '赢了',
    'app.share_lose': '输掉了',
    'app.share_tie': '平局',
    'app.share_revive_title': '筹码不足！',
    'app.share_revive_desc': '分享给好友获得 500 筹码，重返牌桌！',
    'app.share_revive_btn': '🔥 分享复活 (+500)',
    'app.share_later': '以后再说',
    'app.fold': '🏳️ 弃牌',
    'app.check': '✅ 过牌',
    'app.bet': '💰 下注',
    'app.call': '📞 跟注',
    'app.raise': '⬆️ 加注',
    'app.allin': '🔥 全下',
    'app.confirm': '💰 确认',
    'app.amount': '金额',
    'app.round_preflop': '翻牌前',
    'app.round_flop': '翻牌',
    'app.round_turn': '转牌',
    'app.round_river': '河牌',
    'app.round_showdown': '摊牌',
    'app.ai_action_call': '📞 跟注 {amount}',
    'app.ai_action_raise': '⬆️ 加注 {amount}',
    'app.ai_action_bet': '💰 下注 {amount}',
    'app.ai_action_check': '✅ 过牌',
    'app.ai_action_fold': '🏳️ 弃牌',
    'app.ai_action_allin': '🔥 全下！',
    'app.history_title': '对战记录',
    'app.history_empty': '暂无对局记录',
    'app.history_win': '胜利',
    'app.history_lose': '失败',
    'app.history_tie': '平局',
    'app.history_ai': 'AI 对手',
    'app.history_hand': '牌型: ',
    'app.history_prev': '上一页',
    'app.history_next': '下一页',
    'app.history_reload': '重新加载',
    'app.history_timeout': '请求超时，请重试',
    'app.history_error': '加载失败，请重试',
    'app.leaderboard_title': '全球排行榜',
    'app.leaderboard_empty': '暂无玩家数据',
    'app.leaderboard_hint': '完成 3 局以上对战后进入排行',
    'app.leaderboard_champion': '👑 当前冠军',
    'app.leaderboard_winrate': '胜率',
    'app.leaderboard_rank': '排名',
    'app.leaderboard_me': '我',
    'app.leaderboard_stats': '总 {total} 局 · {wins} 胜',
    'app.leaderboard_3_plus': '完成 3 局以上对战后进入排行',
    'app.glory_title': '我的荣耀',
    'app.back': '‹ 返回',
    'app.glory_player': '玩家',
    'app.glory_games': '总局',
    'app.glory_wins': '胜',
    'app.glory_ties': '平',
    'app.glory_winrate': '胜率',
    'app.glory_max_streak': '最高连胜',
    'app.glory_cur_streak': '当前连胜',
    'app.glory_total_chips': '总赢筹码',
    'app.glory_win': '胜',
    'app.glory_lose': '负',
    'app.title_poker_god': '扑克之神',
    'app.title_poker_king': '扑克王者',
    'app.title_poker_legend': '扑克传奇',
    'app.title_poker_master': '扑克大师',
    'app.title_poker_elite': '扑克精英',
    'app.title_poker_veteran': '扑克尖兵',
    'app.title_poker_rookie': '扑克新星',
    'app.you': '你',
    'app.share_earn': '分享赚取',
    'app.share_earn_desc': '分享游戏给好友，赢取奖励筹码！',
    'app.share_earn_btn': '✨ 立即分享',
    'app.error_timeout': '您的网络有问题，请点击重试',
    'app.error_retry': '网络错误，请重试',
    'app.start_failed': '开局失败，请重试',
    'app.share_topbar': '🃏 来玩德州扑克，挑战 世界所有AI 对手！',
    'app.share_result': '{username}在德州扑克比赛中打败了{aiName}模型，试试你行不行！世界级的人机与ai大模型德州扑克大战，你也来试试？',
    'app.win_fold': '{name} 弃牌，你赢了！',
    'app.hand_royal_flush': '皇家同花顺',
    'app.hand_straight_flush': '同花顺',
    'app.hand_four_of_a_kind': '四条',
    'app.hand_full_house': '葫芦',
    'app.hand_flush': '同花',
    'app.hand_straight': '顺子',
    'app.hand_three_of_a_kind': '三条',
    'app.hand_two_pair': '两对',
    'app.hand_one_pair': '一对',
    'app.hand_high_card': '高牌',
    'app.leaderboard_text': '排行榜',
    'app.glory_text': '我的荣耀',
    'app.me_text': '我',
    'app.lang_switch': '语言切换',
    'app.share_earn_chips': '邀请得筹码',
    'app.share_revive_success_title': '分享成功！',
    'app.share_revive_got': '您获得了',
    'app.back_home': '回到首页',
    'app.stats_line': '{games} 局 · 胜率 {rate}%',
    'app.me': '我',
  },
  en: {
    'app.loading': 'Loading...',
    'app.start': 'Start Game',
    'app.starting': 'Starting...',
    'app.replay': 'Play Again',
    'app.share': '📤 Share',
    'app.sharing': 'Sharing...',
    'app.history': '📋 History',
    'app.leaderboard': '🏆 Leaderboard',
    'app.glory': '👑 My Glory',
    'app.balance': 'Your Balance',
    'app.buyin': 'Each player buys in',
    'app.win': '🎉 You beat {name}!',
    'app.lose': '😞 You lost',
    'app.tie': '🤝 Tie!',
    'app.revealing': 'Revealing...',
    'app.thinking': 'Thinking...',
    'app.chips': 'chips',
    'app.your_turn': 'Your turn',
    'app.waiting': 'Waiting for opponent...',
    'app.error_network': 'Network error, please retry',
    'app.error_balance': 'Insufficient balance',
    'app.share_text': 'I {result} {pot} chips in ♠️ Texas Hold\'em{hand}! Play with me!',
    'app.share_win': 'won',
    'app.share_lose': 'lost',
    'app.share_tie': 'tied',
    'app.share_revive_title': 'Out of chips!',
    'app.share_revive_desc': 'Share with friends to get 500 chips back!',
    'app.share_revive_btn': '🔥 Share & Revive (+500)',
    'app.share_later': 'Later',
    'app.fold': '🏳️ Fold',
    'app.check': '✅ Check',
    'app.bet': '💰 Bet',
    'app.call': '📞 Call',
    'app.raise': '⬆️ Raise',
    'app.allin': '🔥 All-in',
    'app.confirm': '💰 Confirm',
    'app.amount': 'Amount',
    'app.round_preflop': 'Preflop',
    'app.round_flop': 'Flop',
    'app.round_turn': 'Turn',
    'app.round_river': 'River',
    'app.round_showdown': 'Showdown',
    'app.ai_action_call': '📞 Call {amount}',
    'app.ai_action_raise': '⬆️ Raise {amount}',
    'app.ai_action_bet': '💰 Bet {amount}',
    'app.ai_action_check': '✅ Check',
    'app.ai_action_fold': '🏳️ Fold',
    'app.ai_action_allin': '🔥 All-in!',
    'app.history_title': 'Game History',
    'app.history_empty': 'No games yet',
    'app.history_win': 'Win',
    'app.history_lose': 'Loss',
    'app.history_tie': 'Tie',
    'app.history_ai': 'vs AI',
    'app.history_hand': 'Hand: ',
    'app.history_prev': '← Prev',
    'app.history_next': 'Next →',
    'app.history_reload': 'Reload',
    'app.history_timeout': 'Request timed out',
    'app.history_error': 'Failed to load',
    'app.leaderboard_title': 'Global Leaderboard',
    'app.leaderboard_empty': 'No players yet',
    'app.leaderboard_hint': 'Play 3+ games to be ranked',
    'app.leaderboard_champion': '👑 Champion',
    'app.leaderboard_winrate': 'Win Rate',
    'app.leaderboard_rank': 'Rank',
    'app.leaderboard_me': 'Me',
    'app.leaderboard_stats': '{total} games · {wins} wins',
    'app.leaderboard_3_plus': 'Play 3+ games to be ranked',
    'app.glory_title': 'My Glory',
    'app.back': '‹ Back',
    'app.glory_player': 'Player',
    'app.glory_games': 'Games',
    'app.glory_wins': 'W',
    'app.glory_ties': 'T',
    'app.glory_winrate': 'Win Rate',
    'app.glory_max_streak': 'Best Streak',
    'app.glory_cur_streak': 'Current Streak',
    'app.glory_total_chips': 'Total Chips Won',
    'app.glory_win': 'W',
    'app.glory_lose': 'L',
    'app.title_poker_god': 'Poker God',
    'app.title_poker_king': 'Poker King',
    'app.title_poker_legend': 'Poker Legend',
    'app.title_poker_master': 'Poker Master',
    'app.title_poker_elite': 'Poker Elite',
    'app.title_poker_veteran': 'Poker Veteran',
    'app.title_poker_rookie': 'Poker Rookie',
    'app.you': 'You',
    'app.share_earn': 'Share & Earn',
    'app.share_earn_desc': 'Share the game with friends to earn bonus chips!',
    'app.share_earn_btn': '✨ Share Now',
    'app.error_timeout': 'Network issue, please retry',
    'app.error_retry': 'Network error, please retry',
    'app.start_failed': 'Failed to start game',
    'app.share_topbar': '♠️ X8 Poker — Challenge all World AI in Texas Hold\'em. Real poker, daily rewards, leaderboard, titles. All inside Telegram!',
    'app.share_result': '{username} defeated {aiName} in Texas Hold\'em! Can you beat the AI? World-class human vs AI battle, come and try!',
    'app.win_fold': '{name} folded, you win!',
    'app.stats_line': '{games} games · {rate}% win rate',
    'app.me': 'Me',
    'app.lang_switch': 'Language',
    'app.share_earn_chips': 'Invite & Earn',
    'app.share_revive_success_title': 'Share success!',
    'app.share_revive_got': 'You got',
    'app.back_home': 'Back to Home',
    'app.leaderboard_text': 'Leaderboard',
    'app.glory_text': 'My Glory',
    'app.me_text': 'Me',
    'app.hand_royal_flush': 'Royal Flush',
    'app.hand_straight_flush': 'Straight Flush',
    'app.hand_four_of_a_kind': 'Four of a Kind',
    'app.hand_full_house': 'Full House',
    'app.hand_flush': 'Flush',
    'app.hand_straight': 'Straight',
    'app.hand_three_of_a_kind': 'Three of a Kind',
    'app.hand_two_pair': 'Two Pair',
    'app.hand_one_pair': 'One Pair',
    'app.hand_high_card': 'High Card',
  },
}

interface LocaleContextValue {
  locale: string
  t: (key: string, params?: Record<string, string>) => string
  toggleLocale: () => void
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'zh',
  t: (key: string) => key,
  toggleLocale: () => {},
})

export function useLocale() {
  return useContext(LocaleContext)
}

function getTelegramLocale(): string {
  try {
    const code = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code
    if (!code) return 'zh'
    const lang = code.toLowerCase()
    if (lang === 'en') return 'en'
    if (lang === 'tl' || lang === 'fil') return 'zh'
    if (lang === 'zh') return 'zh'
    return 'zh'
  } catch {
    return 'zh'
  }
}

const DEV_USER = {
  id: 123456789,
  first_name: 'Test',
  username: 'testuser',
}

const DEV_INIT_DATA = 'user=%7B%22id%22%3A123456789%2C%22first_name%22%3A%22Test%22%2C%22username%22%3A%22testuser%22%7D&auth_date=1699999999&hash=dev_hash'

function App() {
  const { tg, user, isReady } = useTelegram()
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [devProfile, setDevProfile] = useState<any>(null)
  const [profileRefresh, setProfileRefresh] = useState(0)
  const [locale, setLocale] = useState(() => localStorage.getItem('poker_locale') || getTelegramLocale())
  const onGameEnd = useCallback(() => setProfileRefresh(k => k + 1), [])

  const t = (key: string, params?: Record<string, string>) => {
    let text = LOCALE_MAP[locale]?.[key] || LOCALE_MAP.zh[key] || key
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        text = text.replace(`{${k}}`, v)
      }
    }
    return text
  }

  const toggleLocale = () => {
    const next = locale === 'zh' ? 'en' : 'zh'
    setLocale(next)
    localStorage.setItem('poker_locale', next)
  }

  useEffect(() => {
    if (!isReady) return

    // Development mode: always use mock user to get a real JWT
    if (import.meta.env.DEV) {
      console.log('[DEV] Authenticating mock Telegram user')
      authenticate(DEV_USER, DEV_INIT_DATA)
      return
    }

    if (tg && user) {
      tg.ready()
      tg.expand()
      authenticate(user, tg.initData)
    }
  }, [isReady, tg, user])

  const authenticate = async (telegramUser: any, initData: string) => {
    try {
      const res = await fetch('/api/auth/telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ initData, user: telegramUser }),
      })
      const data = await res.json()
      if (data.token) {
        setAuthToken(data.token)
        localStorage.setItem('poker_token', data.token)
      }
      if (data.user) {
        setDevProfile(data.user)
      }
    } catch (err) {
      console.error('Auth failed:', err)
    }
  }

  if (!isReady) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <svg className="h-8 w-8 animate-spin text-blue-400" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <div className="text-lg font-medium text-slate-400">{locale === 'zh' ? '加载中...' : 'Loading...'}</div>
        </div>
      </div>
    )
  }

  return (
    <LocaleContext.Provider value={{ locale, t, toggleLocale }}>
      <div className="h-screen w-full flex flex-col">
        <TopBar authToken={authToken} devProfile={devProfile} refreshKey={profileRefresh} />
        <main className="flex-1 overflow-hidden mx-auto max-w-lg w-full">
          <PokerTableSocket authToken={authToken} userName={devProfile?.firstName || devProfile?.username || '玩家'} onGameEnd={onGameEnd} />
        </main>
      </div>
    </LocaleContext.Provider>
  )
}

export default App
